export const LOCATION_STORAGE_KEY = 'kkisan_location_v1';
export const ENTERED_PINCODE_STORAGE_KEY = 'kkisan_entered_pincode_v1';

/**
 * Reads the active pincode from the default saved address when one exists.
 * Otherwise it falls back to the pincode selected during location setup.
 *
 * @param {object|null} state - Optional Zustand store state
 * @returns {string|null} - 6-digit pincode string or null
 */
export const getActivePincode = (state = null) => {
  // A customer's default delivery address is the primary catalog location.
  // Do not use an arbitrary saved address when none is marked as default.
  const defaultAddress = state?.user_addresses?.find((address) => address.is_default);
  if (defaultAddress?.pincode) {
    const pin = String(defaultAddress.pincode).trim();
    if (/^\d{6}$/.test(pin)) return pin;
  }

  const enteredPin = state?.enteredPincode;
  if (enteredPin && /^\d{6}$/.test(String(enteredPin).trim())) {
    return String(enteredPin).trim();
  }

  // A recently entered pincode takes priority even when it is not serviceable.
  try {
    const enteredPinFromStorage = typeof window !== 'undefined'
      ? localStorage.getItem(ENTERED_PINCODE_STORAGE_KEY)
      : null;
    if (enteredPinFromStorage && /^\d{6}$/.test(enteredPinFromStorage.trim())) {
      return enteredPinFromStorage.trim();
    }
  } catch {
    // Ignore storage access errors
  }

  // 1. Explicit store userLocation (set via location selector / onboarding modal / header)
  if (state?.userLocation) {
    const statePin = state.userLocation.pincode || state.userLocation.location?.pincode;
    if (statePin) {
      const pin = String(statePin).trim();
      if (/^\d{6}$/.test(pin)) return pin;
    }
  }

  // 2. Persisted location in localStorage (chosen at first)
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(LOCATION_STORAGE_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw);
      const pin = parsed?.pincode || parsed?.location?.pincode;
      if (pin) {
        const cleanPin = String(pin).trim();
        if (/^\d{6}$/.test(cleanPin)) return cleanPin;
      }
    }
  } catch {
    // Ignore localStorage parse errors
  }

  return null;
};

/**
 * Returns full active location info (pincode, latitude, longitude, address_id, store_id)
 *
 * @param {object|null} state - Optional Zustand store state
 * @returns {object}
 */
export const getActiveLocation = (state = null) => {
  let address_id = null;
  let pincode = null;
  let latitude = null;
  let longitude = null;
  let store_id = null;

  if (state) {
    const defaultAddress = state.user_addresses?.find((a) => a.is_default);
    if (defaultAddress) {
      address_id = defaultAddress.address_id || defaultAddress.id || null;
      pincode = defaultAddress.pincode || null;
      latitude = defaultAddress.latitude || null;
      longitude = defaultAddress.longitude || null;
    }

    if (state.userLocation) {
      if (!pincode) pincode = state.userLocation.pincode || state.userLocation.location?.pincode || null;
      if (!latitude) latitude = state.userLocation.latitude || state.userLocation.location?.latitude || null;
      if (!longitude) longitude = state.userLocation.longitude || state.userLocation.location?.longitude || null;
      if (!store_id) store_id = state.userLocation.store?.store_id || state.userLocation.store_id || null;
    }
  }

  if (!pincode) {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(LOCATION_STORAGE_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        pincode = parsed?.pincode || parsed?.location?.pincode || null;
        if (!latitude) latitude = parsed?.latitude || parsed?.location?.latitude || null;
        if (!longitude) longitude = parsed?.longitude || parsed?.location?.longitude || null;
        if (!store_id) store_id = parsed?.store?.store_id || parsed?.store_id || null;
      }
    } catch {}
  }

  return { address_id, pincode, latitude, longitude, store_id };
};
